import React, { Suspense, useCallback, useState } from 'react'
import {
  Alert,
  Button,
  Heading,
  Loading,
  SubmitConfirmation,
  TitleController,
} from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import { DataLoader } from './UserInfoDataLoader'
import { ErrorHandler } from './UserInfoErrorHandler'
import { API_ENDPOINTS } from '../../../common/apiEndpoints'
import { UserInfoForm } from './UserInfoForm'
import { UserInfoView } from './UserInfoView'
import { StrongAuthSection } from './StrongAuthSection'
import { ContractDocumentSection } from './ContractDocumentSection'
import {
  ResidentUserAuthLevel,
  UserUpdateRequest,
} from '../../../common/validations'

import { useAPIUpdateUserDetails } from 'api/apiHooks/useAPIUpdateUserEmail'
import { throwOnAccessDenied } from 'api/throwOnAccessDenied'
import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'
import { Section, View, Breakpoint, Permissions } from 'components'
import { ResetPasswordForm } from 'views/Password/ResetPasswordForm'
import { useAPIRemoveUser } from 'api/apiHooks/useAPIRemoveUser'
import { useAPISendChangePasswordResetCode } from 'api/apiHooks/useAPISendChangePasswordResetCode'

type UserInfoViewState = 'view' | 'edit'

export const UserInfo: React.FC = () => {
  const { t } = useTranslation(['common', 'titles', 'userInfo'])
  const { frontPagePath } = useContractPaths()

  const [viewState, setViewState] = useState<UserInfoViewState>('view')
  const [isSetNewPasswordSectionVisible, setIsSetNewPasswordSectionVisible] =
    useState(false)
  const [hasUpdateError, setHasUpdateError] = useState(false)

  const { mutate: removeUser } = useAPIRemoveUser()
  const { mutate: sendChangePasswordResetCode } =
    useAPISendChangePasswordResetCode()
  const { mutate: updateDetails, isPending } = useAPIUpdateUserDetails({
    throwOnError: throwOnAccessDenied,
    onError: () => setHasUpdateError(true),
  })
  const queryClient = useQueryClient()

  const handleUpdateUserDetails = useCallback(
    (userUpdateRequest: UserUpdateRequest) => {
      setHasUpdateError(false)
      updateDetails(userUpdateRequest, {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [API_ENDPOINTS.USER_PROFILE],
          })
          setViewState('view')
        },
      })
    },
    [updateDetails, queryClient]
  )

  return (
    <TitleController title={t('titles:userInfo')}>
      <View>
        <View.Header>
          <View.HeaderLink to={frontPagePath}>
            {t('common:views.home')}
          </View.HeaderLink>

          <View.HeaderTitle>{t('common:views.userInfo')}</View.HeaderTitle>
        </View.Header>

        <View.Sidebar />

        <View.Content className="phone:!mt-6 tablet:border tablet:border-common-gray-5 tablet:p-7 tablet:rounded-lg">
          <Suspense fallback={<Loading variant="details" />}>
            <ErrorHandler>
              <DataLoader>
                {({
                  user,
                  featureFlags,
                  userMetadata,
                  invoiceAddress,
                  authLevel,
                  identificationRequired,
                }) => (
                  <>
                    <Breakpoint show desktop>
                      <h1 className="text-2xl font-medium mb-7 min-tablet:hidden">
                        {t('common:views.userInfo')}
                      </h1>
                    </Breakpoint>

                    {(authLevel === ResidentUserAuthLevel.WEAK ||
                      identificationRequired) && <StrongAuthSection />}

                    <Section divider>
                      <p className="text-lg min-tablet:text-xl font-medium mb-3 min-tablet:mb-6">
                        {user.firstName} {user.lastName}
                      </p>
                      {viewState === 'view' ? (
                        <UserInfoView
                          user={user}
                          invoiceAddress={invoiceAddress}
                        />
                      ) : (
                        <>
                          {hasUpdateError && (
                            <Alert
                              variant="danger"
                              className="w-full justify-start mb-4"
                            >
                              {t('userInfo:errors.updateFailed')}
                            </Alert>
                          )}
                          <UserInfoForm
                            user={user}
                            onSave={handleUpdateUserDetails}
                            onCancel={() => {
                              setHasUpdateError(false)
                              setViewState('view')
                            }}
                            isPending={isPending}
                            userMetadata={userMetadata}
                          />
                        </>
                      )}

                      <div className="flex justify-end mt-4">
                        {viewState === 'view' && (
                          <Button
                            onClick={() => {
                              setHasUpdateError(false)
                              setViewState('edit')
                            }}
                            type="button"
                            secondary
                            className="ml-2"
                            disabled={userMetadata.isImpersonator}
                          >
                            {t('common:edit')}
                          </Button>
                        )}
                      </div>
                    </Section>

                    <Permissions feature="contractDocument">
                      <ContractDocumentSection />
                    </Permissions>

                    <Section>
                      <Heading element="h2" size="xl" semibold className="mb-4">
                        {t('common:userInfo.securitySectionHeader')}
                      </Heading>

                      <div className="flex flex-col gap-3 phone:flex-col phone:gap-4 min-tablet:justify-between items-end">
                        <Button
                          disabled={
                            isSetNewPasswordSectionVisible ||
                            userMetadata.isImpersonator
                          }
                          onClick={() => {
                            sendChangePasswordResetCode()
                            setIsSetNewPasswordSectionVisible(true)
                          }}
                          type="button"
                          secondary
                        >
                          {t('common:userInfo.changePassword')}
                        </Button>

                        {featureFlags.removeUser && (
                          <SubmitConfirmation
                            onSubmit={() => {
                              removeUser()
                            }}
                            onDecline={() => {}}
                            confirmationText={t(
                              'common:userInfo.removeUser.confirmationText'
                            )}
                            acceptButtonText={t('common:yes')}
                            declineButtonText={t('common:no')}
                            triggerButtonProps={{
                              danger: !userMetadata.isImpersonator,
                              disabled: userMetadata.isImpersonator,
                              children: t(
                                'common:userInfo.removeUser.buttonText'
                              ),
                            }}
                          />
                        )}
                      </div>
                    </Section>

                    {isSetNewPasswordSectionVisible && (
                      <Section>
                        <ResetPasswordForm
                          email={user.email ?? ''}
                          onCompletedClick={() =>
                            setIsSetNewPasswordSectionVisible(false)
                          }
                          onCancel={() =>
                            setIsSetNewPasswordSectionVisible(false)
                          }
                        />
                      </Section>
                    )}
                  </>
                )}
              </DataLoader>
            </ErrorHandler>
          </Suspense>
        </View.Content>
      </View>
    </TitleController>
  )
}

export default UserInfo
